<template>
  <div v-click-outside="close" class="property-picker">
    <button
      type="button"
      class="toggle"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <span class="value">
        <template v-for="(label, index) in selectedLabels" :key="index">
          <ChevronRightIcon v-if="index > 0" class="crumb-separator" />
          <span class="crumb">{{ label }}</span>
        </template>
      </span>
      <LockIcon v-if="locked" class="open-indicator" />
      <ChevronDownIcon v-else class="open-indicator" />
    </button>

    <PropertyPickerPanel
      v-if="isOpen && !locked"
      class="panel"
      :types="types"
      :database="database"
      :validate="validate"
      :traversable="traversable"
      :static-options="staticOptions"
      :model-value="modelValue"
      @select="select"
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  ref,
  type PropType,
} from "vue";

import type {
  GraphDatabase,
  GraphDatabaseDictionary,
} from "@ctrl/controller-rpc";

import {
  asRecordType,
  relationTargetDatabase,
  type RecordSchemaField,
} from "@/components/graph/predicates";
import PropertyPickerPanel from "@/components/graph/PropertyPickerPanel.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import LockIcon from "@/components/icons/LockIcon.vue";

type StaticOption = { id: string; label: string };

/**
 * A picker over a type's properties that can traverse relations: fields
 * passing `validate` are selectable end values, other relation fields open
 * the target type as a nested panel on hover — only on demand, since the
 * schema graph may contain cycles. The selection is emitted as a path of
 * column ids from the root type to the chosen field. A static option whose
 * id matches a root property shadows it, pinning that property at the top
 * of the list. Set `traversable` to false for a flat picker over the root
 * type's own properties. When `locked`, the toggle shows a lock and emits
 * `locked-click` instead of opening (e.g. for an entitlement gate).
 */
export default defineComponent({
  components: {
    ChevronDownIcon,
    ChevronRightIcon,
    LockIcon,
    PropertyPickerPanel,
  },
  props: {
    /** All known types, used to resolve relation targets. */
    types: {
      type: Object as PropType<GraphDatabaseDictionary>,
      required: false,
      default: undefined,
    },
    /** The type whose properties root the traversal. */
    database: {
      type: Object as PropType<GraphDatabase>,
      required: false,
      default: undefined,
    },
    /** Whether a field is a valid end value of a path. */
    validate: {
      type: Function as PropType<
        (database: GraphDatabase, field: RecordSchemaField) => boolean
      >,
      required: true,
    },
    /** Whether relation fields can be traversed into nested panels. */
    traversable: {
      type: Boolean,
      default: true,
    },
    /** Synthetic root-level options (selected as a one-id path). */
    staticOptions: {
      type: Array as PropType<StaticOption[]>,
      default: () => [],
    },
    /** The selected path of column ids (or a static option's id). */
    modelValue: {
      type: Array as PropType<string[]>,
      required: true,
    },
    /** Locked pickers show a lock and emit `locked-click` instead of opening. */
    locked: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    "update:modelValue": (_path: string[]) => true,
    "locked-click": () => true,
  },
  setup(props, { emit }) {
    const isOpen = ref(false);

    const close = () => {
      isOpen.value = false;
    };
    const toggle = () => {
      if (props.locked) {
        emit("locked-click");
        return;
      }
      isOpen.value = !isOpen.value;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen.value) close();
    };
    document.addEventListener("keydown", onKeyDown);
    onBeforeUnmount(() => document.removeEventListener("keydown", onKeyDown));

    const select = (path: string[]) => {
      emit("update:modelValue", path);
      close();
    };

    /** Names along the selected path, for the closed toggle. */
    const selectedLabels = computed<string[]>(() => {
      const path = props.modelValue;
      if (path.length === 1) {
        const option = props.staticOptions.find(({ id }) => id === path[0]);
        if (option) return [option.label];
      }
      const labels: string[] = [];
      let database = props.database;
      for (const hop of path) {
        const field = asRecordType(database?.type)?.fields[hop];
        labels.push(field?.name ?? hop);
        database = field
          ? relationTargetDatabase(props.types, field)
          : undefined;
      }
      return labels;
    });

    return {
      close,
      isOpen,
      select,
      selectedLabels,
      toggle,
    };
  },
});
</script>

<style lang="scss" scoped>
.property-picker {
  position: relative;

  .toggle {
    appearance: none;
    background: none;
    border: none;
    margin: 0;
    font: inherit;
    text-align: start;
    cursor: default;
    display: flex;
    align-items: center;
    column-gap: 8px;
    min-width: 210px;
    min-height: 32px;
    padding: 4px 12px;
    box-sizing: border-box;
    border: 1px solid;
    border-radius: $border-radius-l;
    @extend %font-caption-regular;

    @include themed() {
      color: t($primary);
      border-color: t($grey-15);
      background-color: t($bg);
    }

    svg {
      width: 10px;
      height: 10px;
      flex-shrink: 0;
    }

    .value {
      display: flex;
      align-items: center;
      column-gap: 4px;
      flex: 1;
      min-width: 0;
      overflow: hidden;

      .crumb {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .crumb-separator {
        width: 8px;
        height: 8px;
      }
    }
  }

  .panel {
    position: absolute;
    top: calc(100% + 4px);
    inset-inline-end: 0;
    z-index: $z-index-dropdown;
    min-width: 100%;
  }
}
</style>
